import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { map, Observable, of, switchMap } from 'rxjs';
import { AbstractValueAccessorComponent, ValueAccessorUtil } from '@railmybox/shared/util';
import { StationModel, StationService } from '@railmybox/api-booking';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'rmb-station-select',
  templateUrl: './station-select.component.html',
  styleUrls: ['./station-select.component.scss'],
  providers: [...ValueAccessorUtil.getValueAccessorProvider(StationSelectComponent)],
  standalone: false,
})
export class StationSelectComponent extends AbstractValueAccessorComponent<StationModel> implements OnInit {
  private stationService = inject(StationService);

  stations$: Observable<StationModel[]>;
  searchControl: FormControl = new FormControl<string | null>(undefined);
  selectedName: string;
  @Input() filter: string;
  @Input() selected: string;
  @Input() clearable = true;
  @Input() sea?: string;
  readonly TRANSLOCO_PREFIX = 'master.trains';

  async ngOnInit() {
    this.stations$ = this.searchControl.valueChanges.pipe(
      untilDestroyed(this),
      switchMap((query: string) => (query ? this.stationService.listStations({ query }) : of([]))),
      map((x) => x.filter((item) => item.active === true && this.filterBySea(item))),
      map((stations: StationModel[]) =>
        this.filter ? stations.filter((model) => `${model.id} ${model.name}`.toLowerCase().includes(this.filter.toLowerCase())) : stations
      )
    );
  }

  private filterBySea(item: StationModel): boolean {
    if (this.sea === 'sea') {
      return item.sea === true;
    } else if (this.sea === 'noSea') {
      return item.sea === false;
    }
    return true;
  }

  onSearchValueChanged(query: string): void {
    this.searchControl.patchValue(query);
  }

  onSelect(station: StationModel): void {
    this.selectedName = station?.name;
    this.value = station;
    this.onChange(station);
    this.changed.emit(station);
  }

  async writeValue(value: StationModel): Promise<void> {
    super.writeValue(value);

    if (!value) {
      this.selectedName = null;
      return;
    }
    const station = await this.stationService
      .listStations()
      .toPromise()
      .then((filteredValues) => filteredValues.find((station) => station.id === value.id));

    // In case no entry found with that id
    if (!station) {
      return;
    }

    this.value = station;
    this.selectedName = station.name;
  }
}
